/**
 * 학습 관리 페이지 (통합 탭 인터페이스)
 * Learning Management Page with Tabbed Interface
 *
 * 4개의 학습 관련 기능을 하나의 페이지에서 탭으로 관리:
 * - 상담 대시보드: ConsultationDashboardPage
 * - 학습 분석: StudyAnalyticsPage (Phase 3)
 * - 목표 관리: GoalManagementPage
 * - 상담 기록: ConsultationRecordsPage
 */

import React, { useState } from 'react';
import {
  PresentationChartLineIcon,
  AcademicCapIcon,
  ChatBubbleBottomCenterTextIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

// 탭 컴포넌트 import
import StudyAnalyticsPage from './StudyAnalyticsPage';
import GoalManagementPage from './GoalManagementPage';
import ConsultationRecordsPage from './ConsultationRecordsPage';
import ConsultationDashboardPage from './ConsultationDashboardPage';

type TabId = 'study-analytics' | 'goal-management' | 'consultation-records' | 'consultation-dashboard';

interface Tab {
  id: TabId;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  component: React.ComponentType;
}

const tabs: Tab[] = [
  {
    id: 'consultation-dashboard',
    name: '상담 대시보드',
    icon: ChartBarIcon,
    component: ConsultationDashboardPage,
  },
  {
    id: 'study-analytics',
    name: '학습 분석',
    icon: PresentationChartLineIcon,
    component: StudyAnalyticsPage,
  },
  {
    id: 'goal-management',
    name: '목표 관리',
    icon: AcademicCapIcon,
    component: GoalManagementPage,
  },
  {
    id: 'consultation-records',
    name: '상담 기록',
    icon: ChatBubbleBottomCenterTextIcon,
    component: ConsultationRecordsPage,
  },
];

const LearningManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('consultation-dashboard');

  // 현재 활성 탭의 컴포넌트 찾기
  const ActiveTabComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 탭 네비게이션 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex -mb-px space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    transition-colors duration-150 ease-in-out
                    ${isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `}
                    aria-hidden="true"
                  />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* 탭 컨텐츠 */}
      <div className="max-w-7xl mx-auto">
        {ActiveTabComponent && <ActiveTabComponent />}
      </div>
    </div>
  );
};

export default LearningManagementPage;
